<template>
  <div class="bg-white min-h-screen">
    <patient-header />
    <!-- Action buttons section -->
    <section
      class="mx-auto w-10/12 mt-4"
      v-if="!patientGegevens.IsArchived && ssoDetails && ssoDetails.length > 0 && !isUserDeviceManager"
    >
      <div class="row grid-justify-content-right">
        <v-button-dropdown
          :options="ssoDetails"
          :button-props="{ primary: false, inverted: true, alt: true, md: true, hollow: true }"
          @on-select="goToCareProgram"
        >
          Zorgprogramma’s
        </v-button-dropdown>
      </div>
    </section>

    <PatientMessages
      v-if="patientGegevens.PatientId && currentView === 'messages'"
      :patient-id="patientGegevens.PatientId"
      @switch-view="switchView"
    />

    <!-- Dashboard section for all user roles excpet device manager role -->
    <div v-if="!isUserDeviceManager && currentView === 'dashboard'" class="mx-auto w-10/12 pb-8">
      <!-- Patient transfer/wlz status block-->
      <div v-if="getTrasferWlzBlockVisibility()" class="grid grid-rows">
        <div class="relative" v-if="showWlz">
          <Alert
            :showClose="true"
            :html="true"
            :htmlMessage="wlzMessage"
            type="info"
            class="z-40 mt-6"
            @close="() => (showWlz = false)"
          ></Alert>
        </div>
        <div
          class="flex w-auto"
          v-if="
            customerSecurityPolicySettings &&
            customerSecurityPolicySettings.transferredFrom.length > 0 &&
            !customerSecurityPolicySettings.isTransferredToDepartment
          "
        >
          <Alert :html="true" :htmlMessage="policySettingsTransferredFrom" type="info" class="z-40 mt-6"></Alert>
        </div>
        <div class="flex">
          <div
            class="p-4 mt-6 bg-blue-50 inline-flex rounded-md text-sm"
            v-if="customerSecurityPolicySettings && customerSecurityPolicySettings.isTransferredToDepartment"
          >
            <div class="flex mr-2">
              <div class="flex-shrink-0">
                <InformationCircleIcon class="size-5 text-blue-400" aria-hidden="true" />
              </div>
            </div>
            <div>
              Deze patiënt is overgedragen aan afdeling {{ customerSecurityPolicySettings.transferredToDepartmentName }}
            </div>
            <PencilIcon
              class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block text-pdsPrimaryColor ml-2 cursor-pointer"
              aria-hidden="true"
              @click.prevent="showPatientTransferPopUp()"
            />
            <NoSymbolIcon
              class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block text-pdsPrimaryColor cursor-pointer"
              aria-hidden="true"
              @click.prevent="showDeletePatientTransferPopUp()"
            />
            <v-overlay-large
              v-if="showDeletePatientTransfer"
              :title="'Patiëntenoverdracht intrekken?'"
              @close="closeOverlay"
            >
              <p>
                Weet u zeker dat u {{ patientGegevens.Voorletters }} , {{ patientGegevens.Achternaam }} die overgedragen
                is aan {{ customerSecurityPolicySettings.transferredTo.user?.simpleName }} wilt intrekken?
              </p>
              <div class="row grid-justify-content-right">
                <v-button lg hollow transparent @click="closeOverlay"> Annuleren </v-button>
                <v-button class="btn btn--primary btn--lg" @click="deleteUserOfTransferredCustomer()">
                  Intrekken
                </v-button>
              </div>
            </v-overlay-large>
          </div>
        </div>
      </div>
      <!-- Patient licenses details block -->
      <div class="grid grid-rows my-4">
        <div class="md:flex md:items-center md:justify-between">
          <div class="min-w-0 flex mb-4 grid grid-cols-2 w-full">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 sm:truncate sm:tracking-tight">{{ authorizationTitle }}</h2>
            </div>
            <div
              v-if="accountabilities.length > 0 && !patientGegevens.IsDeceased && !patientGegevens.IsArchived"
              class="flex ml-2 mr-2 place-content-end mt-4"
            >
              <div>
                <button
                  id="licenseBtn"
                  @click.prevent="goToNieuweMachtiging"
                  class="flex justify-center py-2 px-4 text-sm font-medium text-white items-center bg-primary-default border border-primary-default rounded-full"
                >
                  <PlusIcon class="w-4 h-4 mr-2" aria-hidden="true" />
                  <span>{{ newAuthorizationBtnText }}</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Tab Navigation for Dashboard View -->
          <div class="flex items-center">
            <div class="flex bg-white rounded-full border border-gray-200 p-1">
              <button
                @click="switchView('dashboard')"
                class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-50 transition-colors"
                :class="{ 'bg-gray-600 text-white': currentView === 'dashboard' }"
              >
                <ClipboardDocumentListIcon class="w-4 h-4 mr-2" />
                Overzicht
              </button>
              <button
                @click="switchView('messages')"
                class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-50 transition-colors"
                :class="{ 'bg-gray-600 text-white': currentView === 'messages' }"
              >
                <ChatBubbleLeftRightIcon class="w-4 h-4 mr-2" />
                Berichten
              </button>
            </div>
          </div>
        </div>
        <div>
          <v-licenses
            v-if="licenseOverviews && licenseOverviews.items.length > 0"
            :is-archived="patientGegevens.IsArchived"
            :license-overviews="licenseOverviews.items"
            :patient-id="patientGegevens.PatientId"
            :is-show-osa-section-feature-enabled="isShowOsaSectionFeatureEnabled"
          />
        </div>
      </div>
      <div
        v-if="licenseOverviews && !licenseOverviews.hasActiveLicenses"
        class="callout p-6 mb-6"
        :class="{ 'mt-6': licenseOverviews && licenseOverviews.items.length > 5 }"
      >
        <div class="row">
          <small
            class="text-pdsPrimaryColor underline cursor-pointer"
            v-html="licenseOverviews.licenseHelperMessage.subMessage"
          />
        </div>
        {{ licenseOverviews.licenseHelperMessage.message }}
        <mds-navigation-link
          v-if="licenseOverviews.licenseHelperMessage.title != ''"
          :title="licenseOverviews.licenseHelperMessage.title"
          :text="licenseOverviews.licenseHelperMessage.title"
          current
        >
          <span slot="submenu">
            <mds-dropdown data-type="menu">
              <div slot="content">
                <mds-navigation-link
                  v-for="item in licenseOverviews.licenseHelperMessage.items"
                  :key="item"
                  href="#"
                  :text="
                    item.institutionName +
                    '&quot; | &quot;' +
                    item.departmentName +
                    '&quot; | &quot; ' +
                    item.treatmentArea
                  "
                  @click="departmentChange(item)"
                />
              </div>
            </mds-dropdown>
          </span>
        </mds-navigation-link>
      </div>
      <!-- Patient recently ordered details block -->
      <v-patient-recently-ordered-products
        :is-active-license="
          licenseOverviews && (licenseOverviews.hasActiveLicenses || licenseOverviews.otherDeptLicense)
        "
        :patient-id="patientGegevens.PatientId"
      />
      <!-- Patient ordered history details block -->
      <v-patient-order-history
        :is-archived="patientGegevens.IsArchived"
        :is-active-license="
          licenseOverviews && (licenseOverviews.hasActiveLicenses || licenseOverviews.otherDeptLicense)
        "
      />
      <!--Patient measurement details based on the portal -->
      <v-patient-measurements v-if="patientGegevens.ShowMeasureMoments && !isRespiratory" />

      <!-- Dashboard section users with device manager role -->
      <section v-if="isUserDeviceManager" class="mt-8">
        <div class="row">
          <div class="col-xs-12 col-md-6">
            <v-heading :level="3"> Apparaten </v-heading>
          </div>
          <div class="col-xs-12 col-md-6 justify-content-end">
            <div>
              <v-button tagelement="a" md cta @click.prevent="$router.push('/apparaattoewijzen')">
                Apparaat toewijzen
              </v-button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Remove the old separate Apparaten section -->
    <!-- <section v-else class="mx-auto w-10/12 pb-8">
      <div class="row mt-4">
        <div class="col-xs-12 col-md-6">
          <v-heading :level="3"> Apparaten </v-heading>
        </div>
        <div class="col-xs-12 col-md-6 justify-content-end">
          <div>
            <v-button tagelement="a" md cta @click.prevent="$router.push('/apparaattoewijzen')">
              Apparaat toewijzen
            </v-button>
          </div>
        </div>
      </div>
    </section> -->
    <generic-modal v-if="showPopUp">
      <template #header> Patiëntinformatie niet compleet </template>
      <template #body>
        <p>
          Niet alle noodzakelijke patiëntinformatie aanwezig, ga naar “Patiëntgegevens wijzigen” om de gegevens aan te
          vullen
        </p>
      </template>
      <template #footer>
        <v-button tagelement="button" md primary @click="editPatientDetails"> Patiëntgegevens wijzigen </v-button>
      </template>
    </generic-modal>
  </div>
</template>

<script>
import router from '@/router';
import axios from 'axios';
import VLicenses from '@/components/sections/licenses/Index';
import PatientHeader from '@/components/sections/patient/PatientHeader';
import PatientMessages from '@/components/sections/notification/PatientMessages';
import VPatientRecentlyOrderedProducts from '@/components/sections/patient/orders/recentOrderedProducts/Index';
import VPatientOrderHistory from '@/components/sections/patient/orders/orderHistory/OrderHistory';
import VButtonDropdown from '@/components/shared/ButtonDropdown';
import VPatientMeasurements from '@/components/sections/patientMeasurements/PatientMeasurements';
import VOverlayLarge from '@/components/shared/OverlayLarge.vue';
import GenericModal from '@/components/shared/GenericModal';
import { map } from 'rxjs/operators';
import { pushDepartmentChangedEvent } from '@/components/sections/navigation/departmentChangedEventStream';
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useStore, mapActions } from 'vuex';
import {
  PlusIcon,
  InformationCircleIcon,
  PencilIcon,
  NoSymbolIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/vue/20/solid';

import {
  currentUserIdentity$,
  ccpSsoDetails$,
  licenseOverviews$,
  customerSecurityPolicySettings$,
  activePpwRecId$,
  activeCustomerId$,
  dispatchCommand,
  navigateToAccountability
} from '@/services';

export default {
  name: 'VPatientdashboard',
  components: {
    VLicenses,
    VPatientOrderHistory,
    VButtonDropdown,
    PatientHeader,
    PatientMessages,
    VPatientMeasurements,
    VPatientRecentlyOrderedProducts,
    VOverlayLarge,
    GenericModal,
    PlusIcon,
    InformationCircleIcon,
    PencilIcon,
    NoSymbolIcon,
    ClipboardDocumentListIcon,
    ChatBubbleLeftRightIcon
  },
  setup() {
    const store = useStore();
    const state = store.state;
    const activePpwRecId = ref(null);
    const activeCustomerId = ref(null);
    const customerSecurityPolicySettings = ref(null);
    const user = ref(null);
    const licenseSubscription = ref(null);
    const licenseOverviews = ref(null);
    const ssoDetails = ref(null);
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    const showWlz = ref(false);
    const currentView = ref('dashboard');

    const wlzMessage = ref(
      `Deze patiënt heeft een WLZ indicatie (
        <a target="_blank" class="underline" href="https://www.rijksoverheid.nl/onderwerpen/verpleeghuizen-en-zorginstellingen/wet-langdurige-zorg-wlz">
          Lees hier meer over WLZ
        </a>).
        Controleert u alstublieft de zwaarte van de indicatie in uw EPD.
        U kunt in de meeste gevallen gebruik maken van de reguliere aanvraagformulieren.
        Indien er sprake is van een indicatie met “verblijf én behandeling” dient u de artikelen te bestellen via
        <a target="_blank" class="underline" href="http://www.medbis.nl">MedBIS</a> of kunt u, indien beschikbaar, gebruik maken van de
        “onvergoede” aanvraagformulieren (met een B ervoor).`
    );

    const policySettingsTransferredFrom = computed(() => {
      return `Deze patiënt is overgedragen aan uw afdeling door ${customerSecurityPolicySettings.value.transferredFrom[0].user.simpleName}`;
    });

    const patientGegevens = computed(() => store.getters['patient/getPatientGegevens']);
    const accountabilities = computed(
      () => store.getters['accountability/getAccountabilities']
    ); /* actually templates */
    const wlzStatus = computed(() => store.getters['patient/getWLZStatus']);
    watch(
      () => wlzStatus.value,
      (newValue, oldValue) => {
        showWlz.value = wlzStatus;
      }
    );
    const patientUpdated = computed(() => store.getters['search/getPatientUpdatedNotification']);

    const fetchWLZStatus = async (patientId) => {
      if (!patientId) return;
      await store.dispatch('patient/fetchWLZStatus', patientId);
    };
    watch(
      patientGegevens,
      (newPatientGegevens) => {
        if (newPatientGegevens?.PatientId) {
          fetchWLZStatus(newPatientGegevens.PatientId);
        }
      },
      { immediate: true }
    );
    const fetchAccountabilities = () => store.dispatch('accountability/fetchAccountabilities');
    const fetchResults = () => store.dispatch('navigation/fetchResults');
    const setShowExtendLicensesBlock = (value) => store.commit('accountability/setShowExtendLicensesBlock', value);
    const startNewAccountability = (value) => store.dispatch('accountability/startNewAccountability', value);
    const selectedDepartmentChange = (value) => store.dispatch('login/selectedDepartmentChange', value);
    const fetchDepartmentResults = () => store.dispatch('login/fetchDepartmentResults');

    activePpwRecId$.subscribe((id) => {
      activePpwRecId.value = id;
    });

    activeCustomerId$.subscribe((id) => {
      activeCustomerId.value = id;
    });

    customerSecurityPolicySettings$.subscribe((settings) => {
      customerSecurityPolicySettings.value = settings;
    });

    currentUserIdentity$.subscribe((identity) => {
      user.value = identity;
    });

    const patientUpdateNotification = () => {
      $emitter.emit('notification', {
        title: 'Gegevens patiënt aangepast',
        description: 'De verzekeringsgegevens van de patiënt zijn aangepast naar de meest actueel bekende gegevens.'
      });
    };
    const allTreatmentAreasIds = user.value.allTreatmentAreas.map((m) => m.treatmentAreaId);

    const hasFarmaceuticaOnly = (...ids) => {
      return allTreatmentAreasIds.length === ids.length && ids.every((id) => allTreatmentAreasIds.includes(id));
    };

    const hasFarmaceuticaAndInfusion = (requiredIds) => {
      return (
        allTreatmentAreasIds.length === requiredIds.length &&
        requiredIds.every((id) => allTreatmentAreasIds.includes(id))
      );
    };
    const newAuthorizationBtnText = computed(() => {
      if (allTreatmentAreasIds && allTreatmentAreasIds.length > 0) {
        if (hasFarmaceuticaOnly('1010') || hasFarmaceuticaAndInfusion(['1010', '1003'])) {
          return 'Nieuw recept';
        }
      }
      return 'Nieuwe machtiging';
    });
    const authorizationTitle = computed(() => {
      switch (newAuthorizationBtnText.value) {
        case 'Nieuw recept':
          return 'Recepten';
        case 'Nieuwe machtiging':
          return 'Machtiging';
        default:
          return 'Machtiging';
      }
    });
    // Subscribe when component is mounted
    onMounted(() => {
      licenseSubscription.value = licenseOverviews$.subscribe((overviews) => {
        licenseOverviews.value = overviews;
      });
    });

    // Unsubscribe when component is unmounted
    onBeforeUnmount(() => {
      if (licenseSubscription.value) {
        licenseSubscription.value.unsubscribe();
      }
    });

    ccpSsoDetails$
      .pipe(
        map((data) => {
          if (data === null) {
            return null;
          }

          return data.map((ssoProgram, index) => {
            return {
              ...ssoProgram,
              id: index,
              title: ssoProgram.treatmentArea
            };
          });
        })
      )
      .subscribe((details) => {
        // E40-8938 currently removing WoundCare, DiaCare and keeping StomaLife as per requirement may be in future will revert back
        ssoDetails.value = details?.filter((item) => item.treatmentArea === 'StomaLife');
      });

    return {
      state,
      patientGegevens,
      accountabilities,
      wlzStatus,
      fetchWLZStatus,
      fetchAccountabilities,
      fetchResults,
      setShowExtendLicensesBlock,
      startNewAccountability,
      selectedDepartmentChange,
      fetchDepartmentResults,
      ...mapActions('patient', ['setEndpoints']),
      activePpwRecId,
      activeCustomerId,
      customerSecurityPolicySettings,
      user,
      licenseOverviews,
      ssoDetails,
      patientUpdated,
      patientUpdateNotification,
      showWlz,
      wlzMessage,
      policySettingsTransferredFrom,
      authorizationTitle,
      newAuthorizationBtnText,
      currentView
    };
  },
  data() {
    return {
      showDeletePatientTransfer: false,
      showPopUp: false,
      deviceHeader: ['', 'Omschrijving', 'Serienummer', 'Locatie', 'Onderhoudsstatus', 'Gepland onderhoud', ''],
      osaOverviewDetails: []
      // Remove currentView from here since it's now in setup()
    };
  },
  computed: {
    isUserDeviceManager() {
      return this.user.isDeviceManager;
    },
    transferredTo() {
      return (this.customerSecurityPolicySettings && this.customerSecurityPolicySettings.transferredTo) || null;
    },
    transferredFrom() {
      return (this.customerSecurityPolicySettings && this.customerSecurityPolicySettings.transferredFrom) || [];
    },
    transferredToDepartment() {
      return (
        (this.customerSecurityPolicySettings && this.customerSecurityPolicySettings.isTransferredToDepartment) || false
      );
    },
    isRespiratory() {
      return this.state.navigation.siteCode === 'MRT';
    },
    isShowOsaSectionFeatureEnabled() {
      return (
        this.licenseOverviews &&
        this.licenseOverviews.hasOsaLicenses &&
        this.$featureFlag.showOsaSectionPatientDashboard
      );
    }
  },
  created() {
    if (!this.isUserDeviceManager) {
      this.loadOsaOverviewDetails();
      this.fetchWLZStatus();
      this.fetchAccountabilities();
      this.fetchResults();
    }
    if (this.patientUpdated) {
      this.patientUpdateNotification();
    }
  },
  methods: {
    switchView(view) {
      this.currentView = view;
    },
    async loadOsaOverviewDetails() {
      await axios.get('osa/GetOsaOverview').then((response) => {
        if (response && response.data && response.data.Success) {
          this.osaOverviewDetails = response.data.OsaOverview;
        }
      });
    },
    goToNieuweMachtiging() {
      this.showPopUp = true;
      let gender = this.patientGegevens.GeslachtId;
      let initial = this.patientGegevens.Voorletters;
      let lastName = this.patientGegevens.Achternaam;
      let dob = this.patientGegevens.GeboorteDatum;
      let policy = this.patientGegevens.Verzekering[0].Polisnummer;
      let payer = this.patientGegevens.Verzekering[0].Verzekeraar.Id;
      let address = this.patientGegevens.WoonAdres.AdresId;
      if (
        gender !== '' &&
        initial !== '' &&
        lastName !== '' &&
        dob !== '' &&
        policy !== '' &&
        payer !== '' &&
        address !== ''
      ) {
        router.push('/nieuwemachtiging');
      }
    },
    editPatientDetails(e) {
      this.showPopUp = false;
      e.preventDefault();
      this.setEndpoints('edit');
      var url = '/patientgegevens';
      router.push(url);
      return false;
    },
    goToCareProgram(id) {
      window.open(this.ssoDetails.find((sso) => sso.id == id).url, '_blank');
    },
    showPatientTransferPopUp() {
      this.$emitter.emit('showPatientTransfer', true);
    },
    closeOverlay() {
      this.showDeletePatientTransfer = false;
    },
    deleteUserOfTransferredCustomer() {
      dispatchCommand('UndoTransferredCustomer', {
        customerId: this.activeCustomerId,
        fromPpwRecId: this.activePpwRecId
      }).then(() => {
        this.closeOverlay();
      });
    },
    showDeletePatientTransferPopUp() {
      this.showDeletePatientTransfer = true;
    },
    getTrasferWlzBlockVisibility() {
      return this.wlzStatus || this.transferredTo || this.transferredFrom.length > 0 || this.transferredToDepartment;
    },
    departmentChange(item) {
      this.selectedDepartmentChange(item.ppwRecId).then((response) => {
        this.fetchDepartmentResults();
        this.fetchResults();
        pushDepartmentChangedEvent({});
      });
    }
  }
};
</script>
